import loggerHelper from './loggerHelper';

let recaptchaSiteKey = null;
let recaptchaLoadPromise = null;
const RECAPTCHA_SRC_PREFIX = 'https://www.google.com/recaptcha/enterprise.js';

const init = (siteKey) => {
  recaptchaSiteKey = siteKey;

  if (recaptchaSiteKey) {
    loadRecaptchaScript();
    hideRecaptchaBadge();
  }
};

const loadRecaptchaScript = () => {
  // If the API is already available, ensure readiness before resolving
  if (window.grecaptcha && window.grecaptcha.enterprise) {
    return new Promise((resolve) => window.grecaptcha.enterprise.ready(resolve));
  }

  if (recaptchaLoadPromise) return recaptchaLoadPromise;

  const existingScript = Array.from(document.scripts)
    .find((s) => s.src && s.src.startsWith(RECAPTCHA_SRC_PREFIX));

  recaptchaLoadPromise = new Promise((resolve, reject) => {
    let settled = false;

    const settle = (fn) => (arg) => {
      if (settled) return;
      settled = true;
      fn(arg);
    };

    const safeResolve = settle(resolve);
    const safeReject = settle((error) => {
      loggerHelper.error(error.message || error);
      reject(error);
    });

    const onLoad = () => {
      loggerHelper.info('reCAPTCHA script loaded successfully');
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        window.grecaptcha.enterprise.ready(safeResolve);
      } else {
        safeReject(new Error('reCAPTCHA Enterprise APIs not available after script load'));
      }
    };

    const onError = () => safeReject(new Error('Failed to load reCAPTCHA script'));

    const timeoutId = setTimeout(() => {
      safeReject(new Error('reCAPTCHA script loading timed out'));
    }, 10000);

    const attach = (script) => {
      const cleanup = () => clearTimeout(timeoutId);
      script.addEventListener('load', () => { cleanup(); onLoad(); });
      script.addEventListener('error', () => { cleanup(); onError(); });
    };

    if (existingScript) {
      attach(existingScript);
    } else {
      const script = document.createElement('script');
      script.src = `${RECAPTCHA_SRC_PREFIX}?render=${recaptchaSiteKey}`;
      script.async = true;
      script.defer = true;
      attach(script);
      document.head.appendChild(script);
    }
  });

  // If the load fails, allow retries in subsequent calls
  recaptchaLoadPromise.catch(() => { recaptchaLoadPromise = null; });

  return recaptchaLoadPromise;
};

const isRecaptchaEnabled = () => {
  return !!recaptchaSiteKey;
};

const shouldUseRecaptcha = (paymentType) => {
  if (!isRecaptchaEnabled()) {
    return false;
  }

  // Only use reCAPTCHA for credit card and bank account tokenization
  const recaptchaPaymentTypes = [
    'card',
    'bank',
    'gocardless',
    'stripe_connect_sepa',
    'stripe_connect_becs',
    'stripe_connect_bacs',
    'maxio_payments_ach',
    'maxio_payments_ach_plaid',
    'maxp_ach_plaid',
    'maxp_ach',
    'maxp_bacs',
    'maxp_eft',
    'maxp_sepa'
  ];

  return recaptchaPaymentTypes.includes(paymentType);
};

const execute = async (paymentType, action = 'tokenization') => {
  if (!isRecaptchaEnabled() || !shouldUseRecaptcha(paymentType)) {
    return null;
  }

  try {
    await loadRecaptchaScript();
    const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action });
    loggerHelper.info('reCAPTCHA token generated successfully');
    return token;
  } catch (error) {
    loggerHelper.error('reCAPTCHA execution failed:', error);
    return null;
  }
};

const hideRecaptchaBadge = () => {
  const styleId = 'cfy-recaptcha-badge-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .grecaptcha-badge {
      visibility: hidden !important;
    }
  `;
  document.head.appendChild(style);
};

export default {
  init,
  execute,
};